import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { db } from '../firebase';
import {
    collection,
    addDoc,
    serverTimestamp,
    Timestamp,
    doc,
    getDoc,
    updateDoc,
    onSnapshot,
} from 'firebase/firestore';
import { FaPaperPlane, FaQuestionCircle, FaExclamationTriangle, FaUsers, FaHome, FaSearch, FaCheck } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { registerMovement } from '../utils/registerMovement';
import { useNotification } from '../context/NotificationContext';
import { esCasaCaseta, esCasaMorosa, useConfiguracionFinanciera, obtenerNombreResidente } from '../utils/morosoUtils';
import { query, where } from 'firebase/firestore';
import '../styles/comunidad.css';
import '../pages/Votaciones.css';
import './CrearComunicado.css';

const CrearComunicado = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const editMode = Boolean(location.state?.editMode || params?.comunicadoId);
    const comunicadoId = location.state?.comunicadoId || params?.comunicadoId || null;
    const comunicadoData = location.state?.comunicadoData || null;

    const [titulo, setTitulo] = useState(comunicadoData?.titulo || '');
    const [categoria, setCategoria] = useState(comunicadoData?.categoria || 'Anuncios Generales');
    const [descripcion, setDescripcion] = useState(comunicadoData?.descripcion || '');
    const [incluirEvento, setIncluirEvento] = useState(false);
    const [tipoEvento, setTipoEvento] = useState('evento');
    const [fechaEvento, setFechaEvento] = useState('');
    const [horaEvento, setHoraEvento] = useState('');
    const [ubicacionEvento, setUbicacionEvento] = useState('');
    const [asistentesEvento, setAsistentesEvento] = useState('');
    const [tipoDestinatarios, setTipoDestinatarios] = useState(() => {
        if (comunicadoData?.tipoDestinatarios) return comunicadoData.tipoDestinatarios;
        if (comunicadoData?.enviarSoloMorosos || comunicadoData?.soloMorosos) return 'morosos';
        if (comunicadoData?.casasHabilitadas?.length > 0) return 'personalizado';
        return 'todos';
    });
    const [casas, setCasas] = useState([]);
    const [selectedCasas, setSelectedCasas] = useState(() => new Set(comunicadoData?.casasHabilitadas || []));
    const [searchTermCasas, setSearchTermCasas] = useState('');
    const [mapResidentesUsuarios, setMapResidentesUsuarios] = useState({});
    const [tooltipData, setTooltipData] = useState({ visible: false, x: 0, y: 0, text: '', abajo: false });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleCasaMouseEnter = (e, casa) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const num = casa.numero || casa.numeroCasa || casa.id;
        const nombre = obtenerNombreResidente(casa) || mapResidentesUsuarios[casa.id] || mapResidentesUsuarios[String(casa.numero || casa.numeroCasa).trim()] || '';
        const texto = nombre ? `Casa ${num} · ${nombre}` : `Casa ${num} · Sin residente registrado`;
        const mostrarAbajo = rect.top < 65;
        setTooltipData({
            visible: true,
            x: rect.left + rect.width / 2,
            y: mostrarAbajo ? rect.bottom + 8 : rect.top - 8,
            text: texto,
            abajo: mostrarAbajo
        });
    };

    const handleCasaMouseLeave = () => {
        setTooltipData(prev => ({ ...prev, visible: false }));
    };

    const { userData, currentUser } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;
    const { showNotification } = useNotification();

    const { cuotaMensual, mesesPermitidos } = useConfiguracionFinanciera(fraccionamientoId);
    const [totalMorosos, setTotalMorosos] = useState(null);

    useEffect(() => {
        if (!fraccionamientoId) return;
        const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const unsubscribe = onSnapshot(ref, (snapshot) => {
            const casasValidas = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(c => !esCasaCaseta(c))
                .sort((a, b) => {
                    const numA = parseInt(a.numero || a.numeroCasa || 0, 10);
                    const numB = parseInt(b.numero || b.numeroCasa || 0, 10);
                    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                    return String(a.numero || a.numeroCasa || '').localeCompare(String(b.numero || b.numeroCasa || ''));
                });
            setCasas(casasValidas);
            const count = casasValidas.filter(c => esCasaMorosa(c, cuotaMensual, mesesPermitidos)).length;
            setTotalMorosos(count);
        }, (err) => console.warn('CrearComunicado: error cargando casas', err));

        // Cargar nombres de residentes registrados en usuarios
        const qUsers = query(collection(db, 'usuarios'), where('fraccionamientoId', '==', fraccionamientoId));
        const unsubUsers = onSnapshot(qUsers, (snap) => {
            const mapping = {};
            snap.docs.forEach(d => {
                const u = d.data() || {};
                const name = (u.nombreCompleto || u.nombre || `${u.nombres || ''} ${u.apellidos || ''}`).trim();
                if (name) {
                    if (u.casaId) mapping[u.casaId] = name;
                    if (u.numeroCasa) mapping[String(u.numeroCasa).trim()] = name;
                }
            });
            setMapResidentesUsuarios(mapping);
        }, (err) => console.warn('CrearComunicado: error cargando usuarios', err));

        return () => {
            unsubscribe();
            unsubUsers();
        };
    }, [fraccionamientoId, cuotaMensual, mesesPermitidos]);

    useEffect(() => {
        if (!editMode || !comunicadoId || !fraccionamientoId) return;
        if (comunicadoData) {
            setTitulo(comunicadoData.titulo || '');
            setCategoria(comunicadoData.categoria || 'Anuncios Generales');
            setDescripcion(comunicadoData.descripcion || '');
            const tipo = comunicadoData.tipoDestinatarios || (comunicadoData.enviarSoloMorosos || comunicadoData.soloMorosos ? 'morosos' : (comunicadoData.casasHabilitadas?.length > 0 ? 'personalizado' : 'todos'));
            setTipoDestinatarios(tipo);
            if (Array.isArray(comunicadoData.casasHabilitadas)) {
                setSelectedCasas(new Set(comunicadoData.casasHabilitadas));
            }
            return;
        }
        const cargarComunicado = async () => {
            try {
                const ref = doc(db, 'fraccionamientos', fraccionamientoId, 'comunicados', comunicadoId);
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const data = snap.data();
                    setTitulo(data.titulo || '');
                    setCategoria(data.categoria || 'Anuncios Generales');
                    setDescripcion(data.descripcion || '');
                    const tipo = data.tipoDestinatarios || (data.enviarSoloMorosos || data.soloMorosos ? 'morosos' : (data.casasHabilitadas?.length > 0 ? 'personalizado' : 'todos'));
                    setTipoDestinatarios(tipo);
                    if (Array.isArray(data.casasHabilitadas)) {
                        setSelectedCasas(new Set(data.casasHabilitadas));
                    }
                }
            } catch (err) {
                console.error('Error al cargar comunicado para edición:', err);
            }
        };
        cargarComunicado();
    }, [editMode, comunicadoId, fraccionamientoId, comunicadoData]);

    const categorias = ['Anuncios Generales', 'Avisos Importantes'];
    const tiposEvento = useMemo(() => ([
        { value: 'evento', label: 'Evento' },
        { value: 'cita', label: 'Cita' },
        { value: 'junta', label: 'Junta' },
        { value: 'mantenimiento', label: 'Mantenimiento' },
    ]), []);

    const filteredCasas = useMemo(() => {
        if (!searchTermCasas.trim()) return casas;
        const q = searchTermCasas.toLowerCase().trim();
        return casas.filter(c => {
            const num = String(c.numero || c.numeroCasa || c.id || '').toLowerCase();
            const calle = String(c.calle || '').toLowerCase();
            const nombre = (obtenerNombreResidente(c) || mapResidentesUsuarios[c.id] || mapResidentesUsuarios[String(c.numero || c.numeroCasa).trim()] || '').toLowerCase();
            return num.includes(q) || calle.includes(q) || nombre.includes(q) || `casa ${num}`.includes(q);
        });
    }, [casas, searchTermCasas, mapResidentesUsuarios]);

    const buildEventDate = (dateStr, timeStr) => {
        if (!dateStr) {
            return null;
        }

        const [year, month, day] = dateStr.split('-').map(Number);
        if ([year, month, day].some((value) => Number.isNaN(value))) {
            return null;
        }

        const date = new Date(year, (month || 1) - 1, day || 1, 0, 0, 0, 0);
        if (timeStr) {
            const [hour, minute] = timeStr.split(':').map(Number);
            date.setHours(hour || 0, minute || 0, 0, 0);
        }
        return date;
    };

    const resolveTargetCollection = (tipo) => (
        String(tipo).toLowerCase() === 'cita' ? 'citas' : 'eventos'
    );

    const handlePublish = async () => {
        if (isSubmitting) {
            return;
        }
        if (!fraccionamientoId) {
            showNotification('Error: No se pudo identificar el fraccionamiento. Inicia sesión de nuevo.', 'error');
            return;
        }

        if (!titulo.trim() || !descripcion.trim()) {
            showNotification('Por favor, completa todos los campos obligatorios.', 'error');
            return;
        }

        if (tipoDestinatarios === 'personalizado' && selectedCasas.size === 0) {
            showNotification('Selecciona al menos un domicilio para enviar el comunicado.', 'warning');
            return;
        }

        let fechaEventoDate = null;
        if (incluirEvento) {
            if (!fechaEvento) {
                showNotification('Selecciona la fecha para el evento en el calendario.', 'error');
                return;
            }
            fechaEventoDate = buildEventDate(fechaEvento, horaEvento);
            if (!fechaEventoDate || Number.isNaN(fechaEventoDate.getTime())) {
                showNotification('La fecha del evento no es válida.', 'error');
                return;
            }
            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            if (fechaEventoDate < hoy) {
                showNotification('La fecha del evento no puede ser en el pasado.', 'error');
                return;
            }
        }

        try {
            setIsSubmitting(true);
            const esMorosos = tipoDestinatarios === 'morosos';
            const casasHabilitadasArr = tipoDestinatarios === 'personalizado' ? Array.from(selectedCasas) : [];
            const numerosCasasHabilitadasArr = tipoDestinatarios === 'personalizado'
                ? casas.filter(c => selectedCasas.has(c.id)).map(c => c.numero || c.numeroCasa || c.id)
                : [];
            
            if (editMode && comunicadoId) {
                // Modo edición: actualizar comunicado existente
                const comunicadoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'comunicados', comunicadoId);
                await updateDoc(comunicadoRef, {
                    titulo: titulo.trim(),
                    categoria,
                    descripcion: descripcion.trim(),
                    tipoDestinatarios,
                    enviarSoloMorosos: esMorosos,
                    soloMorosos: esMorosos,
                    casasHabilitadas: casasHabilitadasArr,
                    numerosCasasHabilitadas: numerosCasasHabilitadasArr,
                    actualizadoEn: serverTimestamp(),
                });

                showNotification('Comunicado actualizado correctamente.', 'success');
                navigate(`/comunidad/comunicados/${encodeURIComponent(categoria)}`);
                return;
            }

            // Modo creación: crear nuevo comunicado
            const comunicadosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'comunicados');
            const comunicadoDoc = await addDoc(comunicadosRef, {
                titulo: titulo.trim(),
                categoria,
                descripcion: descripcion.trim(),
                fecha: serverTimestamp(),
                tipoDestinatarios,
                enviarSoloMorosos: esMorosos,
                soloMorosos: esMorosos,
                casasHabilitadas: casasHabilitadasArr,
                numerosCasasHabilitadas: numerosCasasHabilitadasArr,
            });

            let eventoMetadata = null;

            if (incluirEvento && fechaEventoDate) {
                const targetCollection = resolveTargetCollection(tipoEvento);
                const eventosRef = collection(db, 'fraccionamientos', fraccionamientoId, targetCollection);
                const fechaEventoTexto = fechaEventoDate.toLocaleDateString('es-MX');
                const nuevoEventoRef = await addDoc(eventosRef, {
                    titulo: titulo.trim(),
                    tipo: tipoEvento,
                    descripcion: descripcion.trim() || null,
                    fecha: Timestamp.fromDate(fechaEventoDate),
                    hora: horaEvento || null,
                    ubicacion: ubicacionEvento.trim() || null,
                    asistentes: asistentesEvento ? parseInt(asistentesEvento, 10) : null,
                    notas: descripcion.trim() || null,
                    responsable: userData?.nombre || userData?.nombreCompleto || null,
                    categoria: tipoEvento,
                    fraccionamientoId,
                    creadoPorUid: userData?.uid || null,
                    creadoPorNombre: userData?.nombre || userData?.nombreCompleto || null,
                    creadoEn: serverTimestamp(),
                    actualizadoEn: serverTimestamp(),
                    origen: 'comunicados',
                    comunicadoRelacionadoId: comunicadoDoc.id,
                    tipoDestinatarios,
                    soloMorosos: esMorosos,
                    enviarSoloMorosos: esMorosos,
                    casasHabilitadas: casasHabilitadasArr,
                    numerosCasasHabilitadas: numerosCasasHabilitadasArr,
                });

                eventoMetadata = {
                    eventoId: nuevoEventoRef.id,
                    coleccion: targetCollection,
                    tipoEvento,
                    fechaEvento: fechaEventoDate,
                    ubicacion: ubicacionEvento.trim() || null,
                    fechaLabel: fechaEventoTexto,
                    tipoDestinatarios,
                    soloMorosos: esMorosos,
                    casasHabilitadas: casasHabilitadasArr,
                };

                const comunicadoDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'comunicados', comunicadoDoc.id);
                await updateDoc(comunicadoDocRef, {
                    eventoRelacionadoId: nuevoEventoRef.id,
                    eventoRelacionadoColeccion: targetCollection,
                    eventoRelacionadoFecha: Timestamp.fromDate(fechaEventoDate),
                });

                try {
                    await registerMovement({
                        fraccionamientoId,
                        titulo: `Nuevo evento: ${titulo.trim()}`,
                        descripcion: `${tipoEvento} · ${fechaEventoTexto}`,
                        categoria: 'Eventos',
                        tipoMovimiento: 'evento_creado',
                        origen: 'eventos',
                        metadata: {
                            ...eventoMetadata,
                        },
                        userData,
                        currentUser,
                    });
                } catch (movementError) {
                    console.error('CrearComunicado: no se pudo registrar el movimiento del evento vinculado', movementError);
                }
            }
            
            // Crea una notificación para el dashboard
            await registerMovement({
                fraccionamientoId,
                titulo: `Nuevo comunicado: ${titulo.trim()}`,
                descripcion: `Categoría: ${categoria}`,
                categoria,
                tipoMovimiento: 'nuevo_comunicado',
                origen: 'comunicados',
                metadata: {
                    tituloComunicado: titulo.trim(),
                    categoriaComunicado: categoria,
                    tipoDestinatarios,
                    enviarSoloMorosos: esMorosos,
                    casasHabilitadas: casasHabilitadasArr,
                    ...(eventoMetadata ? { eventoRelacionado: {
                        eventoId: eventoMetadata.eventoId,
                        coleccion: eventoMetadata.coleccion,
                    } } : {}),
                },
                userData,
                currentUser,
            });

            showNotification('¡Comunicado publicado con éxito!', 'success');

            setTitulo('');
            setCategoria('Anuncios Generales');
            setDescripcion('');
            setIncluirEvento(false);
            setTipoEvento('evento');
            setFechaEvento('');
            setHoraEvento('');
            setUbicacionEvento('');
            setSelectedCasas(new Set());
            setTipoDestinatarios('todos');
            
            // Redirigir a la lista de avisos tras publicar
            navigate('/comunidad/avisos'); 

        } catch (error) {
            console.error("Error al publicar el comunicado: ", error);
            showNotification('Hubo un error al publicar el comunicado.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="crear-comunicado-page">
            <div className="comunicado-form-header">
                <button onClick={() => navigate(-1)} className="comunidad-btn-secondary">← Volver</button>
                <div className="header-title-group">
                    <h2>{editMode ? 'Editar Comunicado' : 'Nuevo Comunicado'}</h2>
                    <p>{editMode ? 'Actualiza la información del comunicado' : 'Crea un nuevo anuncio para la comunidad'}</p>
                </div>
            </div>
            <div className="comunicado-form">
                <div className="form-group">
                    <label>
                        Título
                        <span className="tooltip-icon" title="Escribe un título breve y claro que resuma el comunicado (máx. 100 caracteres)">
                            <FaQuestionCircle />
                        </span>
                    </label>
                    <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ingresa el título del comunicado" />
                </div>
                <div className="form-group">
                    <label>
                        Categoría
                        <span className="tooltip-icon" title="Selecciona la categoría que mejor describa tu comunicado para organizarlo correctamente">
                            <FaQuestionCircle />
                        </span>
                    </label>
                    <select value={categoria} onChange={e => setCategoria(e.target.value)}>
                        {categorias.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                </div>
                <div className="form-group">
                    <label>
                        Contenido
                        <span className="tooltip-icon" title="Redacta el mensaje completo del comunicado. Puedes incluir detalles, instrucciones o información relevante">
                            <FaQuestionCircle />
                        </span>
                    </label>
                    <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows="8" placeholder="Escribe aquí el contenido..."></textarea>
                </div>

                {/* Audiencia / Destinatarios */}
                <div className="form-group audiencia-section">
                    <label style={{ fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        Audiencia y Destinatarios
                        <span className="tooltip-icon" title="Elige a quiénes irá dirigido este comunicado y su evento vinculado">
                            <FaQuestionCircle />
                        </span>
                    </label>
                    <div className="audiencia-pill-selector">
                        <button
                            type="button"
                            className={`audiencia-pill ${tipoDestinatarios === 'todos' ? 'active' : ''}`}
                            onClick={() => setTipoDestinatarios('todos')}
                            disabled={isSubmitting}
                        >
                            <FaUsers /> Todos los residentes
                        </button>
                        <button
                            type="button"
                            className={`audiencia-pill ${tipoDestinatarios === 'morosos' ? 'active alert' : ''}`}
                            onClick={() => setTipoDestinatarios('morosos')}
                            disabled={isSubmitting}
                        >
                            <FaExclamationTriangle /> Solo con adeudo (Morosos)
                            {totalMorosos !== null && <span className="audiencia-badge alert">{totalMorosos}</span>}
                        </button>
                        <button
                            type="button"
                            className={`audiencia-pill ${tipoDestinatarios === 'personalizado' ? 'active custom' : ''}`}
                            onClick={() => setTipoDestinatarios('personalizado')}
                            disabled={isSubmitting}
                        >
                            <FaHome /> Selección de domicilios
                            {selectedCasas.size > 0 && <span className="audiencia-badge custom">{selectedCasas.size}</span>}
                        </button>
                    </div>

                    {tipoDestinatarios === 'morosos' && (
                        <div className="morosos-info-box">
                            <FaExclamationTriangle style={{ fontSize: '1.2rem', flexShrink: 0 }} />
                            <div>
                                <strong>Blindaje de privacidad para morosos:</strong> Este comunicado e invitación solo será visible para los {totalMorosos !== null ? totalMorosos : '...'} residentes con adeudo pendiente en el fraccionamiento y para los administradores.
                            </div>
                        </div>
                    )}

                    {tipoDestinatarios === 'personalizado' && (
                        <div className="comunicado-domicilios-panel">
                            <div className="comunicado-domicilios-header">
                                <div className="comunicado-search-wrapper">
                                    <FaSearch className="comunicado-search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Buscar por número o residente..."
                                        value={searchTermCasas}
                                        onChange={(e) => setSearchTermCasas(e.target.value)}
                                        className="comunicado-search-input"
                                    />
                                </div>
                            </div>
                            <div className="comunicado-house-grid" onScroll={handleCasaMouseLeave}>
                                {filteredCasas.map(casa => {
                                    const isSelected = selectedCasas.has(casa.id);
                                    const num = casa.numero || casa.numeroCasa || casa.id;
                                    return (
                                        <div
                                            key={casa.id}
                                            className={`comunicado-house-chip ${isSelected ? 'selected' : ''}`}
                                            onMouseEnter={(e) => handleCasaMouseEnter(e, casa)}
                                            onMouseLeave={handleCasaMouseLeave}
                                            onClick={() => {
                                                const next = new Set(selectedCasas);
                                                if (next.has(casa.id)) next.delete(casa.id);
                                                else next.add(casa.id);
                                                setSelectedCasas(next);
                                            }}
                                        >
                                            {isSelected && (
                                                <span className="comunicado-house-check">
                                                    <FaCheck />
                                                </span>
                                            )}
                                            <span>Casa {num}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="comunicado-domicilios-footer">
                                {selectedCasas.size} de {casas.length} domicilios seleccionados
                            </div>
                        </div>
                    )}
                </div>

                {!editMode && incluirEvento && (
                    <div className="comunicado-evento-fields">
                        <h4>Detalles del evento</h4>
                        <div className="comunicado-evento-grid">
                            <div className="form-group">
                                <label>Tipo de evento</label>
                                <select value={tipoEvento} onChange={(e) => setTipoEvento(e.target.value)}>
                                    {tiposEvento.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Fecha del evento</label>
                                <input
                                    type="date"
                                    value={fechaEvento}
                                    min={new Date().toLocaleDateString('en-CA')}
                                    onChange={(e) => setFechaEvento(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label>Hora</label>
                                <input type="time" value={horaEvento} onChange={(e) => setHoraEvento(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label>Ubicación (opcional)</label>
                                <input type="text" value={ubicacionEvento} onChange={(e) => setUbicacionEvento(e.target.value)} placeholder="Ej. Salón de usos múltiples" />
                            </div>
                            <div className="form-group full-col">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <label style={{ margin: 0 }}>Asistentes esperados (opcional)</label>
                                    {tipoDestinatarios === 'morosos' && totalMorosos !== null && (
                                        <button
                                            type="button"
                                            onClick={() => setAsistentesEvento(String(totalMorosos))}
                                            title="Autocompletar con el total de residentes con adeudo"
                                            style={{
                                                background: '#fff3cd',
                                                border: '1px solid #ffeeba',
                                                color: '#856404',
                                                padding: '2px 8px',
                                                borderRadius: '10px',
                                                fontSize: '0.74rem',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                            }}
                                        >
                                            <FaUsers /> {totalMorosos} con adeudo
                                        </button>
                                    )}
                                    {tipoDestinatarios === 'personalizado' && (
                                        <button
                                            type="button"
                                            onClick={() => setAsistentesEvento(String(selectedCasas.size))}
                                            title="Autocompletar con el número de domicilios seleccionados"
                                            style={{
                                                background: '#e0e7ff',
                                                border: '1px solid #c7d2fe',
                                                color: '#3730a3',
                                                padding: '2px 8px',
                                                borderRadius: '10px',
                                                fontSize: '0.74rem',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                            }}
                                        >
                                            <FaUsers /> {selectedCasas.size} seleccionados
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="number"
                                    min="1"
                                    value={asistentesEvento}
                                    onChange={(e) => setAsistentesEvento(e.target.value)}
                                    placeholder={
                                        tipoDestinatarios === 'morosos' && totalMorosos !== null
                                            ? `Sugerido: ${totalMorosos} con adeudo`
                                            : (tipoDestinatarios === 'personalizado'
                                                ? `Sugerido: ${selectedCasas.size} domicilios`
                                                : "Ej. 25")
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className="form-actions">
                    {!editMode && (
                        <div className="form-actions-toggle comunicado-event-toggle">
                            <label htmlFor="toggle-evento" className="toggle-header">
                                <input
                                    id="toggle-evento"
                                    type="checkbox"
                                    checked={incluirEvento}
                                    onChange={(e) => setIncluirEvento(e.target.checked)}
                                    disabled={isSubmitting}
                                />
                                <span>
                                    {tipoDestinatarios === 'morosos'
                                        ? 'Publicar en Eventos y Citas (Privado para residentes con adeudo)'
                                        : (tipoDestinatarios === 'personalizado'
                                            ? 'Publicar en Eventos y Citas (Solo domicilios seleccionados)'
                                            : 'Publicar también en Eventos y Citas')}
                                </span>
                            </label>
                            {incluirEvento && (
                                <p className="toggle-hint">
                                    {tipoDestinatarios === 'morosos'
                                        ? '🔒 Blindaje de privacidad activo: Este evento se desactiva para la vista pública y SOLO aparecerá en el calendario de residentes con adeudo pendiente y administradores.'
                                        : (tipoDestinatarios === 'personalizado'
                                            ? `🔒 Privado: Este evento solo aparecerá en el calendario de los ${selectedCasas.size} domicilios seleccionados y administradores.`
                                            : 'El comunicado aparecerá como evento en el calendario de la comunidad.')}
                                </p>
                            )}
                        </div>
                    )}
                    <div className="form-actions-buttons">
                        <button className="comunidad-btn-secondary" onClick={() => navigate(-1)} disabled={isSubmitting}>Cancelar</button>
                        <button className="btn-publish" onClick={handlePublish} disabled={isSubmitting}>
                            <FaPaperPlane />
                            {isSubmitting ? (editMode ? 'Actualizando…' : 'Publicando…') : (editMode ? 'Actualizar' : 'Publicar')}
                        </button>
                    </div>
                </div>
            </div>

            {tooltipData.visible && (
                <div
                    className={`zen-fixed-tooltip ${tooltipData.abajo ? 'abajo' : 'arriba'}`}
                    style={{
                        left: `${tooltipData.x}px`,
                        top: `${tooltipData.y}px`
                    }}
                >
                    <span>👤 {tooltipData.text}</span>
                </div>
            )}
        </div>
    );
};

export default CrearComunicado;